import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, CheckCircle, Clock, RefreshCw, TrendingDown, Heart, Users, Activity } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function Alerts() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    if (user?.role === 'agent') {
      fetchAlerts();
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [user, filter]);

  const fetchAlerts = async () => {
    try {
      const response = await api.get(`/alerts?status=${filter}`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/alerts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await api.post('/alerts/analyze', {});
      toast.success(response.data.message);
      fetchAlerts();
      fetchStats();
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Error al analizar check-ins');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.patch(`/alerts/${alertId}/acknowledge`, {});
      toast.success('Alerta reconocida');
      fetchAlerts();
      fetchStats();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error('Error al reconocer alerta');
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.patch(`/alerts/${alertId}/resolve`, {});
      toast.success('Alerta resuelta');
      fetchAlerts();
      fetchStats();
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error('Error al resolver alerta');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'low':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'low_morale':
        return <TrendingDown className="h-5 w-5" />;
      case 'injury_risk':
        return <Activity className="h-5 w-5" />;
      case 'mental_health':
        return <Heart className="h-5 w-5" />;
      case 'relationship_issues':
        return <Users className="h-5 w-5" />;
      default:
        return <AlertTriangle className="h-5 w-5" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'low_morale': 'Moral Baja',
      'injury_risk': 'Riesgo de Lesión',
      'mental_health': 'Salud Mental',
      'relationship_issues': 'Problemas de Relación',
      'playing_time': 'Tiempo de Juego'
    };
    return labels[type] || type;
  };

  if (user?.role !== 'agent') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <Card className="bento-card">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Las alertas solo están disponibles para agentes.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="alerts-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Sistema de Alertas</h1>
            <p className="text-muted-foreground">Monitoreo inteligente del bienestar de tus jugadores</p>
          </div>
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            data-testid="analyze-button"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Analizando...' : 'Analizar Check-ins'}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <p className="text-3xl font-bold text-red-600">{stats.active}</p>
                <p className="text-sm text-muted-foreground mt-1">Activas</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-orange-600">{stats.acknowledged}</p>
                <p className="text-sm text-muted-foreground mt-1">Reconocidas</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
                <p className="text-sm text-muted-foreground mt-1">Resueltas</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-blue-600">
                  {Object.keys(stats.by_type || {}).length}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Tipos</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts List */}
        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="active">Activas ({stats?.active || 0})</TabsTrigger>
            <TabsTrigger value="acknowledged">Reconocidas ({stats?.acknowledged || 0})</TabsTrigger>
            <TabsTrigger value="resolved">Resueltas ({stats?.resolved || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : alerts.length === 0 ? (
            <Card className="bento-card">
              <CardContent className="p-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-muted-foreground">No hay alertas {filter === 'active' ? 'activas' : filter === 'acknowledged' ? 'reconocidas' : 'resueltas'}</p>
              </CardContent>
            </Card>
          ) : (
            alerts.map((alert) => (
              <Card key={alert.alert_id} className="bento-card border-l-4" style={{
                borderLeftColor: alert.severity === 'high' ? '#ef4444' : alert.severity === 'medium' ? '#f97316' : '#eab308'
              }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                          {getTypeIcon(alert.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">{alert.title}</h3>
                            <Badge variant="outline" className={getSeverityColor(alert.severity)}>
                              {alert.severity === 'high' ? 'Alta' : alert.severity === 'medium' ? 'Media' : 'Baja'}
                            </Badge>
                            <Badge variant="outline">{getTypeLabel(alert.type)}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Jugador: <span className="font-medium text-foreground">{alert.player_name}</span>
                          </p>
                        </div>
                      </div>

                      <p className="text-sm mb-3">{alert.description}</p>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-blue-900">
                          <span className="font-medium">Recomendación:</span> {alert.recommendation}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Meses afectados:</span>
                        {alert.affected_months.map((month) => (
                          <Badge key={month} variant="secondary" className="text-xs">
                            {new Date(month + '-01').toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {alert.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledge(alert.alert_id)}
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Reconocer
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleResolve(alert.alert_id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Resolver
                          </Button>
                        </>
                      )}
                      {alert.status === 'acknowledged' && (
                        <Button
                          size="sm"
                          onClick={() => handleResolve(alert.alert_id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Resolver
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Alerts;
